import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import api, { formatError } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Search, Edit, Trash2, Truck } from "lucide-react";

const CATEGORIES = ["medicine", "equipment", "consumables", "laboratory", "radiology", "other"];
const EMPTY = { name: "", company: "", contact: "", email: "", address: "", category: "medicine" };

export default function Vendors() {
  const { t } = useLanguage();
  const { toast } = useToast();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors");
      setVendors(data);
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openAdd = () => { setForm(EMPTY); setEditVendor(null); setShowModal(true); };
  const openEdit = (v) => { setForm({...v}); setEditVendor(v); setShowModal(true); };

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editVendor) {
        await api.put(`/vendors/${editVendor.id}`, form);
        toast({ title: "Vendor updated" });
      } else {
        await api.post("/vendors", form);
        toast({ title: "Vendor added" });
      }
      setShowModal(false); fetchVendors();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this vendor?")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast({ title: "Vendor deleted" });
      fetchVendors();
    } catch (err) {
      toast({ title: "Error", description: formatError(err), variant: "destructive" });
    }
  };

  const filtered = vendors.filter(v =>
    v.name?.toLowerCase().includes(search.toLowerCase()) ||
    v.company?.toLowerCase().includes(search.toLowerCase())
  );

  const catColors = {
    medicine: "bg-emerald-100 text-emerald-800",
    equipment: "bg-blue-100 text-blue-800",
    consumables: "bg-amber-100 text-amber-800",
    laboratory: "bg-violet-100 text-violet-800",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-sans">{t("vendors")}</h1>
          <p className="text-slate-500 text-sm mt-1">Manage medicine and equipment vendors</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" onClick={openAdd} data-testid="add-vendor-btn">
          <Plus size={16} /> Add Vendor
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search vendors..." className="pl-9 border-slate-200" value={search} onChange={e => setSearch(e.target.value)} data-testid="vendor-search" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold text-slate-700">Vendor Name</TableHead>
              <TableHead className="font-semibold text-slate-700">Company</TableHead>
              <TableHead className="font-semibold text-slate-700">Contact</TableHead>
              <TableHead className="font-semibold text-slate-700">Category</TableHead>
              <TableHead className="font-semibold text-slate-700">Payment</TableHead>
              <TableHead className="font-semibold text-slate-700">{t("actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">{t("loading")}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-400">
                <Truck size={32} className="mx-auto mb-2 opacity-30" />
                <p>No vendors found</p>
              </TableCell></TableRow>
            ) : filtered.map(v => (
              <TableRow key={v.id} className="table-row-hover" data-testid={`vendor-row-${v.id}`}>
                <TableCell>
                  <p className="font-medium text-slate-900 text-sm">{v.name}</p>
                  {v.email && <p className="text-xs text-slate-500">{v.email}</p>}
                </TableCell>
                <TableCell className="text-sm text-slate-700">{v.company}</TableCell>
                <TableCell className="text-sm text-slate-600">{v.contact}</TableCell>
                <TableCell>
                  <Badge className={`capitalize text-xs ${catColors[v.category] || "bg-slate-100 text-slate-700"}`}>{v.category}</Badge>
                </TableCell>
                <TableCell>
                  <Badge className={`text-xs ${v.payment_status === "clear" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>
                    {v.payment_status}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900" onClick={() => openEdit(v)} data-testid={`edit-vendor-${v.id}`}>
                      <Edit size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-rose-600" onClick={() => handleDelete(v.id)} data-testid={`delete-vendor-${v.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-sans">{editVendor ? "Edit Vendor" : "Add Vendor"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-slate-700">Vendor Name</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 border-slate-200" required data-testid="vendor-name-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Company</Label>
                <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} className="mt-1 border-slate-200" required data-testid="vendor-company-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Contact</Label>
                <Input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} className="mt-1 border-slate-200" required data-testid="vendor-contact-input" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 border-slate-200" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Category</Label>
                <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white capitalize" data-testid="vendor-cat-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm text-slate-700">Address</Label>
                <Input value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="mt-1 border-slate-200" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white" disabled={saving} data-testid="save-vendor-btn">
                {saving ? "Saving..." : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
